<script setup lang="ts">
import { computed, watch } from 'vue';
import CheckboxTree from '@/components/common/CheckboxTree.vue';
import { Button } from '@/components/ui/button';
import { defaultReportFarmIds } from '@/composables/inventory/inventoryReport';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { MEDICINE_REPORT_MAX_FARMS, type CheckboxTreeNode } from '@/types';

const farmIds = defineModel<number[]>('value', { required: true });

defineEmits<{ next: [] }>();

/** Every farm selected in the dashboard's location picker is offered; the dashboard's own selection is never written from here. */
const { selectedFarms: offeredFarms } = useInventoryScope();

const atLimit = computed(() => farmIds.value.length >= MEDICINE_REPORT_MAX_FARMS);

/** The cap is expressed as `disabled` rather than enforced on click, so the tree cannot hand back an over-long selection; a ticked farm is never disabled, so the last pick can be undone. */
const nodes = computed<CheckboxTreeNode[]>(() =>
    offeredFarms.value.map((farm) => ({
        key: String(farm.farm_id),
        label: farm.name,
        disabled: atLimit.value && !farmIds.value.includes(farm.farm_id),
    }))
);
const selectedKeys = computed(() => farmIds.value.map(String));

function applySelection(keys: string[]) {
    farmIds.value = keys.map(Number);
}

/** As many farms as the cap allows, first in the list first; past the cap "all" can never be ticked, so the button says where it stops. */
const selectableCount = computed(() => Math.min(offeredFarms.value.length, MEDICINE_REPORT_MAX_FARMS));
const isAllSelected = computed(() => selectableCount.value > 0 && farmIds.value.length >= selectableCount.value);

function toggleAll() {
    farmIds.value = isAllSelected.value ? [] : defaultReportFarmIds(offeredFarms.value);
}

// A fresh wizard starts with the first farms of the selection ticked; coming back from the next step keeps what was ticked.
// Watched rather than read once on mount: on a direct page load the farm list arrives after this step is created.
// A farm the location picker no longer offers is dropped from the ticked ones, so it is never sent unseen.
watch(
    offeredFarms,
    (farms) => {
        const offered = new Set(farms.map((farm) => farm.farm_id));
        if (farmIds.value.some((id) => !offered.has(id))) {
            farmIds.value = farmIds.value.filter((id) => offered.has(id));
        }
        if (farmIds.value.length === 0 && farms.length > 0) {
            farmIds.value = defaultReportFarmIds(farms);
        }
    },
    { immediate: true, flush: 'post' }
);
</script>

<template>
    <div class="w-full space-y-4">
        <h2 class="font-semibold text-xl text-center">{{ $t('views.health.inventory.report.selectFarms.title') }}</h2>
        <div class="flex justify-center">
            <div class="max-w-[80%] w-full">
                <h3 class="text-center pb-2 font-semibold">{{ $t('views.health.inventory.report.selectFarms.description', { max: MEDICINE_REPORT_MAX_FARMS }) }}</h3>
                <div class="flex justify-end pb-2">
                    <Button
                        variant="outline"
                        size="sm"
                        :disabled="selectableCount === 0"
                        :title="
                            !isAllSelected && offeredFarms.length > MEDICINE_REPORT_MAX_FARMS ? $t('views.health.inventory.report.selectFarms.selectAllLimit', { max: MEDICINE_REPORT_MAX_FARMS }) : undefined
                        "
                        @click="toggleAll"
                    >
                        {{ isAllSelected ? $t('global.uiElements.multiSelect.deselectAllButtonLabel') : $t('global.uiElements.multiSelect.selectAllButtonLabel') }}
                    </Button>
                </div>
                <!-- CheckboxTree is h-full: without its own auto-height wrapper it would size to this column, heading included, and spill over the button. -->
                <div>
                    <CheckboxTree :nodes="nodes" :selected-keys="selectedKeys" :empty-label="$t('views.health.inventory.report.selectFarms.noFarms')" @update:selected-keys="applySelection" />
                </div>
                <p v-if="atLimit" class="pt-2 text-center text-sm text-amber-600">{{ $t('views.health.inventory.report.selectFarms.limitReached', { max: MEDICINE_REPORT_MAX_FARMS }) }}</p>
            </div>
        </div>
        <div class="flex justify-center">
            <Button :disabled="farmIds.length === 0" class="min-w-32" @click="$emit('next')">{{ $t('views.reports.nextButtonLabel') }}</Button>
        </div>
    </div>
</template>
